import axios from "axios";
import { toast } from "sonner";

// Global timeout for all requests (45s — datasets grandes en prod pueden requerir tiempo)
axios.defaults.timeout = 45000;

// Deduplicate error toasts - track recent messages
let lastToastMsg = "";
let lastToastTime = 0;

const showUniqueToast = (msg) => {
  const now = Date.now();
  if (msg === lastToastMsg && now - lastToastTime < 3000) return;
  lastToastMsg = msg;
  lastToastTime = now;
  toast.error(msg);
};

// Response interceptor: retry GET + global error handling
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Skip handling for cancelled requests
    if (axios.isCancel(error)) return Promise.reject(error);

    const config = error.config;

    // Auto-retry GET requests on network errors or 5xx (max 2 retries)
    // NEVER retry POST/PUT/DELETE (avoid duplicate data)
    // Skip retries entirely when caller sets config._noRetry = true (e.g. health watchdog)
    const method = (config?.method || "").toLowerCase();
    const isRetryable =
      method === "get" &&
      !config?._noRetry &&
      (config?._retryCount || 0) < 2;
    const isNetworkError = !error.response;
    const isServerError = error.response?.status >= 500;

    if (isRetryable && (isNetworkError || isServerError)) {
      config._retryCount = (config._retryCount || 0) + 1;
      const delay = 800 * config._retryCount;
      await new Promise((resolve) => setTimeout(resolve, delay));
      return axios(config);
    }

    // Network error (no response) — only show if not retrying
    if (!error.response) {
      if (error.code === "ECONNABORTED") {
        showUniqueToast("La solicitud tardó demasiado. Intenta de nuevo.");
      }
      return Promise.reject(error);
    }

    const status = error.response.status;

    // 401: Token expired - handled by AuthContext
    if (status === 401) {
      return Promise.reject(error);
    }

    // 403: Permission denied - always show
    if (status === 403) {
      showUniqueToast("No tienes permiso para esta acción");
      return Promise.reject(error);
    }

    // 500+: Server errors - ONLY show toast for user-initiated actions (POST/PUT/DELETE)
    // GET requests that fail are handled by individual pages with .catch() fallbacks
    if (status >= 500) {
      const reqMethod = (error.config?.method || "").toUpperCase();
      if (reqMethod !== "GET") {
        showUniqueToast("Error del servidor. Intenta de nuevo.");
      }
      return Promise.reject(error);
    }

    // Let other errors (400, 404, 422) be handled by individual components
    return Promise.reject(error);
  }
);

export default axios;
